import type { LocalizedDict } from '../index'

// Admin Settings → Integrations → Research tools
// (features/admin/ResearchTools.tsx). "Alura" is a product name.

export type ResearchToolsStrings = {
  title: string
  encryptedNote: string
  simulationNotice: string
  loading: string
  loadError: string
  savedToast: string
  savedToastDescription: string
  saveErrorToast: string
  unknownError: string
  removedToast: string
  removeErrorToast: string
  providerSubtitle: string
  credentialsLabel: string
  updated: (when: string) => string
  lastChecked: (when: string) => string
  neverChecked: string
  remove: string
  confirmRemove: string
  yesRemove: string
  apiKey: string
  apiKeyHint: string
  savingNote: string
  cancel: string
  saving: string
  rotateCredentials: string
  saveCredentials: string
  testConnection: string
  testing: string
  testOk: string
  testFailed: string
  workspace: string
  plan: string
  creditsRemaining: string
  creditsUnknown: string
  testErrors: Record<string, string>
}

export const researchToolsI18n: LocalizedDict<ResearchToolsStrings> = {
  en: {
    title: 'Research tools',
    encryptedNote: 'API keys are encrypted at rest and remain write-only.',
    simulationNotice:
      'Simulation mode — research tool keys require the live backend (real-API build). Saving is disabled here.',
    loading: 'Loading…',
    loadError: 'Could not load research tool keys.',
    savedToast: 'Alura key saved',
    savedToastDescription: 'The API key is encrypted and only its last four characters are shown.',
    saveErrorToast: 'Could not save the key',
    unknownError: 'Unknown error',
    removedToast: 'Alura key removed',
    removeErrorToast: 'Could not remove the key',
    providerSubtitle: 'Etsy research data · one key for all of PEXIFY',
    credentialsLabel: 'Alura key',
    updated: (when) => `Updated ${when}`,
    lastChecked: (when) => `Last checked ${when}`,
    neverChecked: 'Not checked yet',
    remove: 'Remove',
    confirmRemove: 'Remove the Alura key?',
    yesRemove: 'Yes, remove',
    apiKey: 'API key',
    apiKeyHint: 'Create it in Alura under Settings → API keys. Encrypted at rest and never displayed again.',
    savingNote: 'Saving the key turns nothing on. No screen uses Alura data yet.',
    cancel: 'Cancel',
    saving: 'Saving…',
    rotateCredentials: 'Replace key',
    saveCredentials: 'Save key',
    testConnection: 'Test connection',
    testing: 'Testing…',
    testOk: 'Connected to Alura',
    testFailed: 'Alura did not answer correctly',
    workspace: 'Workspace',
    plan: 'Plan',
    creditsRemaining: 'Credits left this month',
    creditsUnknown: 'not reported',
    testErrors: {
      unauthorized: 'Alura did not accept this key. Check that it was copied in full, or create a new one.',
      forbidden: 'Alura refused the call — the key lacks a permission, or the plan or workspace blocks it.',
      rate_limited: 'Alura is limiting calls right now. Try again in a minute.',
      transport: 'Could not reach Alura. Try again shortly.',
      key_unreadable: 'The saved key could not be read. Enter it again.',
    },
  },
  tr: {
    title: 'Araştırma araçları',
    encryptedNote: 'API anahtarları şifreli saklanır ve yalnızca yazılabilir; geri okunamaz.',
    simulationNotice:
      'Simülasyon modu — araştırma aracı anahtarları için canlı sunucu gerekir (gerçek API sürümü). Burada kaydetme kapalı.',
    loading: 'Yükleniyor…',
    loadError: 'Araştırma aracı anahtarları yüklenemedi.',
    savedToast: 'Alura anahtarı kaydedildi',
    savedToastDescription: 'API anahtarı şifreli saklanır ve yalnızca son dört karakteri gösterilir.',
    saveErrorToast: 'Anahtar kaydedilemedi',
    unknownError: 'Bilinmeyen hata',
    removedToast: 'Alura anahtarı silindi',
    removeErrorToast: 'Anahtar silinemedi',
    providerSubtitle: 'Etsy araştırma verisi · tüm PEXIFY için tek anahtar',
    credentialsLabel: 'Alura anahtarı',
    updated: (when) => `Güncellendi: ${when}`,
    lastChecked: (when) => `Son deneme: ${when}`,
    neverChecked: 'Henüz denenmedi',
    remove: 'Sil',
    confirmRemove: 'Alura anahtarı silinsin mi?',
    yesRemove: 'Evet, sil',
    apiKey: 'API anahtarı',
    apiKeyHint: 'Alura\'da Ayarlar → API anahtarları bölümünden oluşturun. Şifreli saklanır ve bir daha gösterilmez.',
    savingNote: 'Anahtarı kaydetmek hiçbir şeyi açmaz. Henüz Alura verisini kullanan bir ekran yok.',
    cancel: 'Vazgeç',
    saving: 'Kaydediliyor…',
    rotateCredentials: 'Anahtarı değiştir',
    saveCredentials: 'Anahtarı kaydet',
    testConnection: 'Bağlantıyı dene',
    testing: 'Deneniyor…',
    testOk: 'Alura\'ya bağlandı',
    testFailed: 'Alura doğru yanıt vermedi',
    workspace: 'Çalışma alanı',
    plan: 'Paket',
    creditsRemaining: 'Bu ay kalan kredi',
    creditsUnknown: 'bildirilmedi',
    testErrors: {
      unauthorized: 'Alura bu anahtarı kabul etmedi. Tamamının kopyalandığından emin olun ya da yeni anahtar oluşturun.',
      forbidden: 'Alura çağrıyı reddetti — anahtarın izni eksik ya da paket/çalışma alanı engelliyor.',
      rate_limited: 'Alura şu an çağrıları sınırlıyor. Bir dakika sonra tekrar deneyin.',
      transport: 'Alura\'ya ulaşılamadı. Biraz sonra tekrar deneyin.',
      key_unreadable: 'Kayıtlı anahtar okunamadı. Anahtarı yeniden girin.',
    },
  },
}
